import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// Log environment for debugging
console.log('App environment:', import.meta.env.MODE);

// Add meta tags for SEO
const meta = document.createElement('meta');
meta.name = 'description';
meta.content = 'Professional portfolio of a software engineer showcasing projects, skills, and experience in web development, app design, and software solutions.';
if (!document.querySelector('meta[name="description"]')) {
  document.head.appendChild(meta);
}

// Error Boundary for better error handling
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { 
      hasError: true,
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with the error
    this.setState({ error });
    // You can also log the error to an error reporting service
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    // You can also log errors to an error reporting service here
    // logErrorToMyService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div style={{ padding: '20px', color: 'red' }}>Something went wrong. Please check the console for details.</div>;
    }
    return this.props.children;
  }
}

// Initialize the app
const initApp = () => {
  console.log('Initializing application...');
  
  try {
    // Check if we're in the browser environment
    if (typeof window === 'undefined') {
      throw new Error('Not running in a browser environment');
    }

    const rootElement = document.getElementById('root');
    
    if (!rootElement) {
      throw new Error('Root element not found');
    }

    console.log('Root element found, creating React root...');
    
    const root = createRoot(rootElement);
    
    console.log('Rendering application...');
    
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
    
    console.log('Application rendered successfully');
    
    // Hide the loading message after the app is rendered
    const loadingElement = document.getElementById('loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    
  } catch (error) {
    console.error('Critical error during app initialization:', error);
    
    // Show error to the user
    const errorElement = document.createElement('div');
    errorElement.style.padding = '20px';
    errorElement.style.color = 'red';
    errorElement.style.backgroundColor = '#fff';
    errorElement.style.position = 'fixed';
    errorElement.style.top = '0';
    errorElement.style.left = '0';
    errorElement.style.right = '0';
    errorElement.style.bottom = '0';
    errorElement.style.zIndex = '10000';
    errorElement.style.overflow = 'auto';
    
    errorElement.innerHTML = `
      <h2>Application Error</h2>
      <p>An error occurred while loading the application:</p>
      <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow-x: auto;">
${error instanceof Error ? error.stack : String(error)}
      </pre>
      <p>Please try refreshing the page. If the problem persists, contact support.</p>
      <button onclick="window.location.reload()" style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
        Refresh Page
      </button>
    `;
    
    // Clear any existing content
    document.body.innerHTML = '';
    document.body.appendChild(errorElement);
    
    // Also log to console
    console.error('Application initialization failed:', error);
  }
};

// Start the app
initApp();
